import { API_ENDPOINTS } from "../config";
import { http } from "../utils/http";
import { toFormData } from "../utils/formData";
import { store } from "../State/store/configureStore";
import { closeProgress, showProgress } from "../State/actions/uploadProgress";
import { isAbsoluteURL } from "../utils/utils";
import StorageService from "./storageService";
import { API_URL_KEY } from "../utils/http";

class FileService {
  constructor() {
    this.filesLoaded = new Map(); // Use Map for better performance
    this.filePromises = new Map(); // Store promises to avoid duplicate requests
  }

  async uploadFiles(type, files, thumbnails, enableCloud, compress) {
    const data = {
      mediaType: type,
      files,
      thumbnailFiles: thumbnails,
    };
    if (compress) {
      data.compress = true;
    }

    let api = API_ENDPOINTS.fileUpload();
    if (enableCloud) {
      api += "?enableCloud=true";
    }

    const data_1 = await http.request(
      api,
      "post",
      toFormData(data),
      null,
      (progressEvent) => {
        // const uploadProgress = Math.round(
        //   progressEvent.loaded * 100 / progressEvent.total
        // );
        // if (!uploadProgress || uploadProgress == 100) {
        //   store.dispatch(closeProgress());
        // } else {
        //   store.dispatch(showProgress(uploadProgress));
        // }
        // this.setState({ uploadProgress });
        const uploadProgress = Math.round(
          (progressEvent.loaded * 100) / progressEvent.total
        );
        store.dispatch(showProgress(uploadProgress));
      }
    );
    store.dispatch(closeProgress());
    if (data_1 && data_1._embedded) {
      return data_1._embedded.contents;
    }
    return [];
  }

  /**
   * Async bulk upload files - returns jobId immediately, processes in background
   * @param {string} type - Media type
   * @param {Array} files - Files to upload
   * @param {Array} thumbnails - Thumbnail files
   * @param {Function} onUploadProgress - Optional progress callback (0-100)
   * @returns {Promise<{jobId: string}>} - Job ID for polling
   */
  async bulkUploadFiles(type, files, thumbnails, onUploadProgress) {
    const data = {
      mediaType: type,
      files,
      thumbnailFiles: thumbnails,
    };

    const response = await http.request(
      API_ENDPOINTS.bulkUploadFiles,
      "post",
      toFormData(data),
      null,
      onUploadProgress ? (progressEvent) => {
        const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        onUploadProgress(percent);
      } : null
    );
    return response;
  }

  /**
   * Get bulk upload job status
   * @param {string} jobId - Job ID from bulkUploadFiles
   * @returns {Promise<{isFinished: boolean, results: Content[]}>}
   */
  async getBulkUploadStatus(jobId) {
    return http.request(API_ENDPOINTS.bulkUploadFilesStatus(jobId), "get");
  }

  async getFile(url) {
    // Return cached file if it exists
    if (this.filesLoaded.has(url)) {
      return Promise.resolve(this.filesLoaded.get(url));
    }

    // Return existing promise if request is already in progress
    if (this.filePromises.has(url)) {
      return this.filePromises.get(url);
    }

    if (isAbsoluteURL(url)) {
      const absoluteUrlPromise = Promise.resolve(url);
      this.filesLoaded.set(url, url);
      return absoluteUrlPromise;
    }

    // Check if URL is relative and needs base URL prefix
    let finalUrl = url;
    if (url.startsWith("/api/")) {
      const baseURL = StorageService.getFromLocalStorage(API_URL_KEY);
      finalUrl = baseURL + url;
      //console.log(`[FileService] Converting relative URL to absolute:`, {
      //  originalUrl: url,
      //  baseURL: baseURL,
      //  finalUrl: finalUrl,
      //});
    }

    //console.log(`[FileService] Making request to:`, finalUrl);

    // Create new promise for the request
    const filePromise = http
      .request(finalUrl, "get", null, null, null, null, "blob")
      .then((data) => {
        if (!data) {
          // Remove from promises map on error
          this.filePromises.delete(url);
          throw new Error(`No data received for: ${url}`);
        }

        const dataConv = URL.createObjectURL(data);
        //console.log(`[FileService] Successfully loaded and created blob URL:`, {
        //  originalUrl: url,
        //  finalUrl: finalUrl,
        //  blobUrl: dataConv,
        //  blobSize: data.size,
        //});
        // Cache the result
        this.filesLoaded.set(url, dataConv);
        // Remove from promises map since request is complete
        this.filePromises.delete(url);
        return dataConv;
      })
      .catch((error) => {
        console.log(`[FileService] Error loading image:`, {
          originalUrl: url,
          finalUrl: finalUrl,
          error: error.message || error,
        });
        // Remove from promises map on error
        this.filePromises.delete(url);
        throw error;
      });

    // Store the promise to prevent duplicate requests
    this.filePromises.set(url, filePromise);

    return filePromise;
  }

  async getRawFile(url) {
    // getRawFile is used for direct blob downloads, typically not cached
    // as the raw blob data might be used differently each time
    if (isAbsoluteURL(url)) {
      return Promise.resolve(url);
    }

    const data = await http.request(url, "get", null, null, null, null, "blob");
    return data;
  }

  // Clear cache method for memory management
  clearCache() {
    // Revoke all object URLs to prevent memory leaks
    this.filesLoaded.forEach((objectUrl, url) => {
      if (objectUrl.startsWith("blob:")) {
        URL.revokeObjectURL(objectUrl);
      }
    });

    this.filesLoaded.clear();
    this.filePromises.clear();
  }

  // Clear a specific file from cache
  clearFileFromCache(url) {
    if (this.filesLoaded.has(url)) {
      const objectUrl = this.filesLoaded.get(url);
      if (objectUrl.startsWith("blob:")) {
        URL.revokeObjectURL(objectUrl);
      }
      this.filesLoaded.delete(url);
    }

    if (this.filePromises.has(url)) {
      this.filePromises.delete(url);
    }
  }

  deleteFile(fiLeKey) {
    return http.request(API_ENDPOINTS.contentUrl(fiLeKey), "delete");
  }

  // Debug method to check cache status
  getCacheStatus() {
    return {
      cachedFiles: this.filesLoaded.size,
      pendingRequests: this.filePromises.size,
      cachedUrls: Array.from(this.filesLoaded.keys()),
      pendingUrls: Array.from(this.filePromises.keys()),
    };
  }
}

const fileService = new FileService();

// Expose fileService for debugging in development
if (process.env.NODE_ENV === "development") {
  window.fileService = fileService;
}

export default fileService;
